import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import BottomNav from '../components/BottomNav';

const USER_CATS = [
  { id: 'hands', label: 'Hands', emoji: '🥁', color: '#FF5500' },
  { id: '4way', label: '4-Way', emoji: '🎯', color: '#00B4D8' },
  { id: 'grooves', label: 'Grooves', emoji: '🎵', color: '#7B2FBE' },
  { id: 'book', label: 'Book', emoji: '📖', color: '#F9A825' },
  { id: 'sheets', label: 'Sheets', emoji: '📄', color: '#22C55E' },
  { id: 'others', label: 'Others', emoji: '⭐', color: '#EC4899' },
];

function DonutChart({ data, size = 180 }) {
  const total = data.reduce((a, d) => a + d.value, 0);
  if (total === 0) return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#1C1916', border: '1px solid #2A2520', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3A3530', fontSize: 13 }}>
      Sem dados
    </div>
  );

  const r = size / 2 - 20;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const strokeWidth = 28;

  let offset = 0;
  const segments = data.filter(d => d.value > 0).map(d => {
    const pct = d.value / total;
    const dash = pct * circumference;
    const gap = circumference - dash;
    const seg = { ...d, dash, gap, offset, pct };
    offset += dash;
    return seg;
  });

  const exportPDF = () => {
    const periodLabel = period === 'week' ? 'This Week'
      : period === 'month' ? 'This Month'
      : `${customStart} to ${customEnd}`;

    const rows = filteredSessions.map(s => {
      const info = getExerciseInfo(s);
      return `
        <tr>
          <td>${new Date(s.completed_at).toLocaleDateString('pt-PT')}</td>
          <td>${info.title}</td>
          <td style="color:${info.catColor}">${info.catEmoji} ${info.catName}</td>
          <td>${formatTime(s.duration_seconds||0)}</td>
          <td>${s.bpm || '—'} BPM</td>
          ${s.notes ? `<td><em>${s.notes}</em></td>` : '<td>—</td>'}
        </tr>`;
    }).join('');

    const catRows = chartData.map(d => `
      <tr>
        <td>${d.emoji} ${d.name}</td>
        <td>${d.count} sessions</td>
        <td>${formatTime(d.seconds)}</td>
        <td>${Math.round((d.seconds/totalSeconds)*100)}%</td>
      </tr>`).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Drumnasium — Estatísticas</title>
        <style>
          body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 900px; margin: 0 auto; }
          h1 { color: #FF5500; font-size: 28px; margin-bottom: 4px; }
          h2 { font-size: 16px; color: #555; font-weight: normal; margin-bottom: 24px; }
          .summary { display: flex; gap: 20px; margin-bottom: 28px; }
          .card { background: #f5f5f5; border-radius: 10px; padding: 16px 24px; text-align: center; flex: 1; }
          .card .val { font-size: 28px; font-weight: 800; color: #FF5500; }
          .card .lbl { font-size: 12px; color: #888; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
          th { background: #FF5500; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          tr:nth-child(even) td { background: #fafafa; }
          h3 { font-size: 16px; color: #333; margin: 24px 0 12px; border-bottom: 2px solid #FF5500; padding-bottom: 6px; }
          .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; }
        </style>
      </head>
      <body>
        <h1>🥁 DRUMNASIUM</h1>
        <h2>Training Report — ${periodLabel}</h2>

        <div class="summary">
          <div class="card"><div class="val">${totalSessions}</div><div class="lbl">Sessions</div></div>
          <div class="card"><div class="val">${formatTime(totalSeconds)}</div><div class="lbl">Total Time</div></div>
          <div class="card"><div class="val">${maxBpm||'—'}</div><div class="lbl">Max BPM</div></div>
        </div>

        <h3>Summary by Category</h3>
        <table>
          <thead><tr><th>Category</th><th>Sessions</th><th>Time</th><th>%</th></tr></thead>
          <tbody>${catRows}</tbody>
        </table>

        <h3>Logs</h3>
        <table>
          <thead><tr><th>Date</th><th>Exercise</th><th>Category</th><th>Duration</th><th>BPM</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Background circle */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1C1916" strokeWidth={strokeWidth}/>
      {segments.map((seg, i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none"
          stroke={seg.color} strokeWidth={strokeWidth}
          strokeDasharray={`${seg.dash} ${seg.gap}`}
          strokeDashoffset={-seg.offset}
          strokeLinecap="butt"
        />
      ))}
    </svg>
  );
}

export default function StatsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('week'); // week | month | custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [exercises, setExercises] = useState({});
  const [userExercises, setUserExercises] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [selectedExercises, setSelectedExercises] = useState([]);

  useEffect(() => { loadData(); }, [period, customStart, customEnd]);

  const loadData = async () => {
    setLoading(true);
    const now = new Date();
    let startDate, endDate;

    if (period === 'week') {
      startDate = new Date(); startDate.setDate(now.getDate() - 7);
      endDate = now;
    } else if (period === 'month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
    } else if (period === 'custom' && customStart && customEnd) {
      startDate = new Date(customStart + 'T00:00:00');
      endDate = new Date(customEnd + 'T23:59:59');
    } else {
      startDate = new Date(); startDate.setDate(now.getDate() - 7);
      endDate = now;
    }

    let query = supabase.from('sessions')
      .select('*')
      .eq('user_id', user.id)
      .gte('completed_at', startDate.toISOString())
      .order('completed_at', { ascending: false });

    if (endDate) query = query.lte('completed_at', endDate.toISOString());

    const { data: sess } = await query;

    const { data: cats } = await supabase.from('categories').select('*').order('sort_order');
    const { data: exs } = await supabase.from('exercises').select('id, title, categories(name, color)');
    const { data: uexs } = await supabase.from('user_exercises').select('id, title, user_category, user_subcategory_id').eq('user_id', user.id);
    const { data: allCats } = await supabase.from('categories').select('id, name, color').order('sort_order');

    if (sess) setSessions(sess);
    if (cats) setCategories(cats);
    if (exs) {
      const map = {};
      exs.forEach(e => { map[e.id] = e; });
      setExercises(map);
    }
    // Store all app categories for proper color/name lookup
    if (allCats) {
      const catMap = {};
      allCats.forEach(c => { catMap[c.id] = c; });
      // Update exercises with full category info
    }
    if (uexs) {
      const map = {};
      uexs.forEach(e => { map[e.id] = e; });
      setUserExercises(map);
    }
    setLoading(false);
  };

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - date) / 86400000);
    if (diff === 0) return `Today ${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`;
    if (diff === 1) return 'Yesterday';
    return `${date.getDate()}/${date.getMonth()+1}`;
  };

  // Build chart data — merge app categories + user categories
  const getExerciseInfo = (sess) => {
    if (sess.exercise_id && exercises[sess.exercise_id]) {
      const ex = exercises[sess.exercise_id];
      return {
        title: ex.title,
        catName: ex.categories?.name || 'Other',
        catColor: ex.categories?.color || '#888',
        catEmoji: '🥁',
        isUser: false,
      };
    }
    if (sess.user_exercise_id && userExercises[sess.user_exercise_id]) {
      const ex = userExercises[sess.user_exercise_id];
      // Robust lookup: match by id, fallback to first cat (Hands), never silently "Others"
      const uc = (ex.user_category && USER_CATS.find(c => c.id === ex.user_category)) || USER_CATS[0];
      return {
        title: ex.title,
        catName: uc.label,
        catColor: uc.color,
        catEmoji: uc.emoji,
        isUser: true,
        userCatId: ex.user_category || uc.id,
      };
    }
    // Session references a deleted user exercise — show generic My Studies
    if (sess.user_exercise_id) {
      return { title: 'My Studies', catName: 'My Studies', catColor: '#FF5500', catEmoji: '⭐', isUser: true };
    }
    return { title: 'Exercise', catName: 'Other', catColor: '#888', catEmoji: '🥁', isUser: false };
  };

  // Aggregate by category
  const catData = {};
  sessions.forEach(s => {
    const info = getExerciseInfo(s);
    const key = info.catName;
    if (!catData[key]) catData[key] = { name: key, color: info.catColor, emoji: info.catEmoji, seconds: 0, count: 0 };
    catData[key].seconds += s.duration_seconds || 0;
    catData[key].count += 1;
  });

  const chartData = Object.values(catData).sort((a, b) => b.seconds - a.seconds);
  const totalSeconds = sessions.reduce((a, s) => a + (s.duration_seconds || 0), 0);
  const totalSessions = sessions.length;
  const maxBpm = sessions.length ? Math.max(...sessions.map(s => s.bpm || 0)) : 0;

  // Filter logs
  const filteredSessions = filterCat === 'all' ? sessions : sessions.filter(s => {
    const info = getExerciseInfo(s);
    return info.catName === filterCat;
  });

  const exportPDF = () => {
    const periodLabel = period === 'week' ? 'This Week'
      : period === 'month' ? 'This Month'
      : `${customStart} to ${customEnd}`;

    const rows = filteredSessions.map(s => {
      const info = getExerciseInfo(s);
      return `
        <tr>
          <td>${new Date(s.completed_at).toLocaleDateString('pt-PT')}</td>
          <td>${info.title}</td>
          <td style="color:${info.catColor}">${info.catEmoji} ${info.catName}</td>
          <td>${formatTime(s.duration_seconds||0)}</td>
          <td>${s.bpm || '—'} BPM</td>
          ${s.notes ? `<td><em>${s.notes}</em></td>` : '<td>—</td>'}
        </tr>`;
    }).join('');

    const catRows = chartData.map(d => `
      <tr>
        <td>${d.emoji} ${d.name}</td>
        <td>${d.count} sessions</td>
        <td>${formatTime(d.seconds)}</td>
        <td>${Math.round((d.seconds/totalSeconds)*100)}%</td>
      </tr>`).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Drumnasium — Estatísticas</title>
        <style>
          body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 900px; margin: 0 auto; }
          h1 { color: #FF5500; font-size: 28px; margin-bottom: 4px; }
          h2 { font-size: 16px; color: #555; font-weight: normal; margin-bottom: 24px; }
          .summary { display: flex; gap: 20px; margin-bottom: 28px; }
          .card { background: #f5f5f5; border-radius: 10px; padding: 16px 24px; text-align: center; flex: 1; }
          .card .val { font-size: 28px; font-weight: 800; color: #FF5500; }
          .card .lbl { font-size: 12px; color: #888; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
          th { background: #FF5500; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          tr:nth-child(even) td { background: #fafafa; }
          h3 { font-size: 16px; color: #333; margin: 24px 0 12px; border-bottom: 2px solid #FF5500; padding-bottom: 6px; }
          .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; }
        </style>
      </head>
      <body>
        <h1>🥁 DRUMNASIUM</h1>
        <h2>Training Report — ${periodLabel}</h2>

        <div class="summary">
          <div class="card"><div class="val">${totalSessions}</div><div class="lbl">Sessions</div></div>
          <div class="card"><div class="val">${formatTime(totalSeconds)}</div><div class="lbl">Total Time</div></div>
          <div class="card"><div class="val">${maxBpm||'—'}</div><div class="lbl">Max BPM</div></div>
        </div>

        <h3>Summary by Category</h3>
        <table>
          <thead><tr><th>Category</th><th>Sessions</th><th>Time</th><th>%</th></tr></thead>
          <tbody>${catRows}</tbody>
        </table>

        <h3>Logs</h3>
        <table>
          <thead><tr><th>Date</th><th>Exercise</th><th>Category</th><th>Duration</th><th>BPM</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };


  // Get exercises with sessions for export
  const exercisesForExport = () => {
    if (!filteredSessions || filteredSessions.length === 0) return [];
    const map = {};
    filteredSessions.forEach(s => {
      const info = getExerciseInfo(s);
      const key = s.exercise_id ? `e${s.exercise_id}` : s.user_exercise_id ? `u${s.user_exercise_id}` : null;
      if (!key) return;
      if (!map[key]) map[key] = { key, title: info.title, catName: info.catName, catColor: info.catColor, sessions: [] };
      map[key].sessions.push({ date: s.completed_at, bpm: s.bpm, duration: s.duration_seconds, notes: s.notes });
    });
    return Object.values(map).sort((a,b) => a.title.localeCompare(b.title));
  };

  const generatePDF = (exsToUse) => {
    const COLORS = ['#FF5500','#4A9EFF','#22C55E','#F9A825','#A78BFA','#EC4899','#14B8A6','#F97316'];
    const exs = exsToUse || exercisesForExport().filter(e => selectedExercises.includes(e.key));
    if (exs.length === 0) return;
    const periodLabel = period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : `${customStart} to ${customEnd}`;

    // Build chart data
    const allDates = [...new Set(exs.flatMap(e => e.sessions.map(s => s.date)))].sort();
    const allBpms = exs.flatMap(e => e.sessions.map(s => s.bpm).filter(Boolean));
    const globalMin = allBpms.length ? Math.min(...allBpms) : 0;
    const globalMax = allBpms.length ? Math.max(...allBpms) : 200;
    const W = 680, H = 260, padL = 50, padR = 20, padT = 28, padB = 48;
    const yRange = globalMax - globalMin || 20;
    const toX = d => allDates.length > 1 ? padL + (allDates.indexOf(d) / (allDates.length-1)) * (W-padL-padR) : padL + (W-padL-padR)/2;
    const toY = v => padT + (1-(v-globalMin+yRange*0.05)/(yRange*1.1))*(H-padT-padB);

    const yLabels = [0,0.25,0.5,0.75,1].map(t => {
      const val = Math.round(globalMin + t*yRange);
      const y = toY(val);
      return `<line x1="${padL}" y1="${y}" x2="${W-padR}" y2="${y}" stroke="#ddd" stroke-width="1" stroke-dasharray="4,4"/>
              <text x="${padL-6}" y="${y+4}" text-anchor="end" font-size="11" fill="#666">${val}</text>`;
    }).join('');

    const xLabels = allDates.filter((_,i,arr) => i===0||i===arr.length-1||i%Math.max(1,Math.floor(arr.length/6))===0).map(d => {
      const dt = new Date(d);
      return `<text x="${toX(d)}" y="${H-padB+18}" text-anchor="middle" font-size="10" fill="#666">${dt.getDate()}/${dt.getMonth()+1}</text>`;
    }).join('');

    const lines = exs.map((ex,ei) => {
      const col = COLORS[ei%COLORS.length];
      const sorted = [...ex.sessions].filter(s=>s.bpm).sort((a,b)=>new Date(a.date)-new Date(b.date));
      if (sorted.length < 1) return '';
      const pts = sorted.map(s=>`${toX(s.date)},${toY(s.bpm)}`).join(' ');
      const dots = sorted.map(s=>`<circle cx="${toX(s.date)}" cy="${toY(s.bpm)}" r="4" fill="${col}"/>`).join('');
      return `<polyline points="${pts}" fill="none" stroke="${col}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>${dots}`;
    }).join('');

    const legend = exs.map((ex,ei) => {
      const col = COLORS[ei%COLORS.length];
      return `<span style="display:inline-flex;align-items:center;gap:6px;margin:0 12px 6px 0;">
        <span style="display:inline-block;width:24px;height:3px;background:${col};border-radius:2px;vertical-align:middle;"></span>
        <span style="font-size:12px;color:#333;">${ex.title}</span></span>`;
    }).join('');

    const logsHtml = exs.map((ex,ei) => {
      const col = COLORS[ei%COLORS.length];
      const sorted = [...ex.sessions].sort((a,b)=>new Date(b.date)-new Date(a.date));
      const rows = sorted.map(s=>`<tr>
        <td>${new Date(s.date).toLocaleDateString('en-GB')}</td>
        <td>${s.bpm?s.bpm+' BPM':'—'}</td>
        <td>${s.duration?Math.floor(s.duration/60)+'m '+('0'+s.duration%60).slice(-2)+'s':'—'}</td>
        <td style="color:#555;font-style:italic;">${s.notes||'—'}</td>
      </tr>`).join('');
      return `<div style="margin-bottom:20px;page-break-inside:avoid;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
          <span style="display:inline-block;width:24px;height:3px;background:${col};border-radius:2px;"></span>
          <span style="font-family:'Barlow Condensed',Arial;font-size:14px;font-weight:900;color:${col};letter-spacing:1px;">${ex.title}</span>
        </div>
        <table><thead><tr><th>Date</th><th>BPM</th><th>Duration</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
      <title>Drumnasium — Training Report</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=Barlow:wght@400;600&display=swap');
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Barlow',Arial,sans-serif;color:#1A1510;background:#fff;padding:32px 40px;max-width:800px;margin:0 auto;}
        .header{display:flex;align-items:center;justify-content:space-between;padding-bottom:14px;border-bottom:3px solid #FF5500;margin-bottom:22px;}
        .brand h1{font-family:'Barlow Condensed',Arial;font-size:28px;font-weight:900;color:#FF5500;letter-spacing:4px;line-height:1;}
        .brand p{font-size:9px;color:#888;letter-spacing:3px;font-family:'Barlow Condensed',Arial;font-weight:700;margin-top:2px;}
        .badge{background:#1A1510;color:#FF5500;padding:5px 12px;border-radius:20px;font-family:'Barlow Condensed',Arial;font-weight:700;font-size:11px;letter-spacing:1px;border:1.5px solid #FF5500;}
        h2{font-family:'Barlow Condensed',Arial;font-size:12px;font-weight:900;color:#FF5500;letter-spacing:2px;text-transform:uppercase;margin:16px 0 8px;}
        .chart-box{background:#111009;border-radius:8px;padding:12px;margin-bottom:12px;}
        .legend{display:flex;flex-wrap:wrap;padding:8px 12px;background:#f8f6f3;border-radius:6px;margin-bottom:16px;}
        table{width:100%;border-collapse:collapse;margin-bottom:6px;font-size:12px;}
        th{background:#1A1510;color:#FF5500;padding:7px 9px;text-align:left;font-family:'Barlow Condensed',Arial;font-weight:700;font-size:10px;letter-spacing:1px;text-transform:uppercase;}
        td{padding:6px 9px;border-bottom:1px solid #f0ebe3;}
        tr:nth-child(even) td{background:#fafaf8;}
        .footer{margin-top:24px;padding-top:10px;border-top:1px solid #eee;display:flex;justify-content:space-between;font-size:10px;color:#aaa;}
        .footer strong{font-family:'Barlow Condensed',Arial;color:#FF5500;letter-spacing:2px;font-size:11px;}
      </style></head><body>
      <div class="header">
        <div class="brand"><h1>DRUMNASIUM</h1><p>PERCUSSION TRAINING</p></div>
        <div class="badge">TRAINING REPORT · ${periodLabel.toUpperCase()}</div>
      </div>
      <h2>BPM Evolution</h2>
      <div class="chart-box">
        <svg width="100%" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
          ${yLabels}${xLabels}
          <text x="14" y="${H/2}" text-anchor="middle" font-size="11" fill="#888" transform="rotate(-90,14,${H/2})">BPM</text>
          <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${H-padB}" stroke="#555" stroke-width="1.5"/>
          <line x1="${padL}" y1="${H-padB}" x2="${W-padR}" y2="${H-padB}" stroke="#555" stroke-width="1.5"/>
          ${lines}
        </svg>
      </div>
      <div class="legend">${legend}</div>
      <h2>Session Logs by Exercise</h2>
      ${logsHtml}
      <div class="footer">
        <strong>DRUMNASIUM</strong>
        <span>Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</span>
      </div>
    </body></html>`;

    const win = window.open('', '_blank');
    if (!win) { alert('Please allow popups for this site to export PDF.'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch(e) {} }, 800);
  };

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:'var(--app-width, 860px)', margin:'0 auto', paddingBottom:80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .fu { animation: fadeUp 0.3s ease forwards; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
        .section { background: #1C1916; border: 1px solid #2A2520; border-radius: 12px; padding: 18px 16px; margin-bottom: 12px; }
      `}</style>
      <div className="bg-grid"/>

      {/* Header */}
      <div style={{ padding:'52px 20px 16px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:34, fontWeight:900, letterSpacing:1, color:'#F0EBE3', lineHeight:1 }}>STATISTICS</h1>
          <button onClick={() => period !== 'custom' ? generatePDF(exercisesForExport()) : generatePDF()}
            disabled={filteredSessions.length === 0 || (period === 'custom' && selectedExercises.length === 0)}
            style={{ background:'#FF5500', color:'#fff', padding:'8px 14px', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:12, letterSpacing:1, opacity: (filteredSessions.length === 0 || (period==='custom'&&selectedExercises.length===0)) ? 0.4 : 1 }}>
            PDF ↓
          </button>
        </div>

        {/* Period toggle */}
        <div style={{ display:'flex', background:'#1C1916', borderRadius:10, padding:4, marginTop:14, gap:4 }}>
          {[['week','Week'],['month','Month'],['custom','Custom']].map(([p,l]) => (
            <button key={p} onClick={() => { setPeriod(p); if (p === 'custom') setShowDatePicker(true); }}
              style={{ flex:1, padding:'9px 4px', background:period===p?'#FF5500':'transparent', color:period===p?'#fff':'#7A7268', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, letterSpacing:1 }}>{l}</button>
          ))}
        </div>

        {/* Custom date picker */}
        {period === 'custom' && (
          <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'center' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, marginBottom:4 }}>START</div>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:13, padding:'8px 10px', outline:'none' }}/>
            </div>
            <div style={{ color:'#5A5248', paddingTop:18, fontSize:12 }}>→</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, marginBottom:4 }}>END</div>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                style={{ width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:13, padding:'8px 10px', outline:'none' }}/>
            </div>
          </div>
        )}

        {/* Exercise selection for Custom — inline */}
        {period === 'custom' && customStart && customEnd && !loading && (
          <div style={{ marginTop:12 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ fontSize:10, color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>EXERCISES FOR PDF</div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setSelectedExercises(exercisesForExport().map(e=>e.key))}
                  style={{ background:'transparent', color:'#FF5500', fontSize:10, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, border:'none', cursor:'pointer' }}>ALL</button>
                <span style={{ color:'#3A3530', fontSize:10 }}>·</span>
                <button onClick={() => setSelectedExercises([])}
                  style={{ background:'transparent', color:'#5A5248', fontSize:10, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, border:'none', cursor:'pointer' }}>NONE</button>
              </div>
            </div>
            {exercisesForExport().length === 0 ? (
              <div style={{ fontSize:12, color:'#5A5248', fontStyle:'italic', padding:'8px 0' }}>No sessions found in this period.</div>
            ) : exercisesForExport().map((ex, i) => {
              const COLORS = ['#FF5500','#4A9EFF','#22C55E','#F9A825','#A78BFA','#EC4899','#14B8A6','#F97316'];
              const col = COLORS[i % COLORS.length];
              const selected = selectedExercises.includes(ex.key);
              return (
                <label key={ex.key} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', background:selected?'#1C1208':'#1A1510', borderRadius:8, marginBottom:6, cursor:'pointer', border:`1.5px solid ${selected?col:'#2A231C'}` }}>
                  <input type="checkbox" checked={selected} onChange={e => {
                    if (e.target.checked) setSelectedExercises(p => [...p, ex.key]);
                    else setSelectedExercises(p => p.filter(k => k !== ex.key));
                  }} style={{ accentColor:col, width:16, height:16, flexShrink:0 }}/>
                  <div style={{ width:14, height:3, background:col, borderRadius:2, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ex.title}</div>
                    <div style={{ fontSize:10, color:'#7A7268' }}>{ex.sessions.length} session{ex.sessions.length!==1?'s':''}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding:'12px 16px', position:'relative', zIndex:1 }}>

        {/* Summary cards */}
        <div className="fu" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
          {[
            { label:'Sessions', value:totalSessions, icon:'🥁' },
            { label:'Time', value:formatTime(totalSeconds), icon:'⏱' },
            { label:'Max BPM', value:maxBpm||'—', icon:'⚡' },
          ].map(s => (
            <div key={s.label} style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:12, padding:'14px 10px', textAlign:'center' }}>
              <div style={{ fontSize:20, marginBottom:6 }}>{s.icon}</div>
              <div style={{ fontFamily:"'Barlow Condensed'", fontSize:24, fontWeight:800, color:'#FF5500', lineHeight:1 }}>{s.value}</div>
              <div style={{ fontSize:10, color:'#5A5248', fontWeight:600, marginTop:4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Donut chart + legend */}
        {chartData.length > 0 && (
          <div className="section fu" style={{ animationDelay:'0.05s', opacity:0 }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:12, fontWeight:800, letterSpacing:2, color:'#7A7268', marginBottom:16, textTransform:'uppercase' }}>
              Time by Category
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:20 }}>
              {/* Donut */}
              <div style={{ position:'relative', flexShrink:0 }}>
                <DonutChart data={chartData.map(d => ({ value: d.seconds, color: d.color }))} size={160}/>
                <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  <div style={{ fontFamily:"'Barlow Condensed'", fontSize:22, fontWeight:800, color:'#F0EBE3', lineHeight:1 }}>{formatTime(totalSeconds)}</div>
                  <div style={{ fontSize:10, color:'#7A7268', marginTop:2 }}>TOTAL</div>
                </div>
              </div>
              {/* Legend */}
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                {chartData.map(d => {
                  const pct = Math.round((d.seconds / totalSeconds) * 100);
                  const exportPDF = () => {
    const periodLabel = period === 'week' ? 'This Week'
      : period === 'month' ? 'This Month'
      : `${customStart} to ${customEnd}`;

    const rows = filteredSessions.map(s => {
      const info = getExerciseInfo(s);
      return `
        <tr>
          <td>${new Date(s.completed_at).toLocaleDateString('pt-PT')}</td>
          <td>${info.title}</td>
          <td style="color:${info.catColor}">${info.catEmoji} ${info.catName}</td>
          <td>${formatTime(s.duration_seconds||0)}</td>
          <td>${s.bpm || '—'} BPM</td>
          ${s.notes ? `<td><em>${s.notes}</em></td>` : '<td>—</td>'}
        </tr>`;
    }).join('');

    const catRows = chartData.map(d => `
      <tr>
        <td>${d.emoji} ${d.name}</td>
        <td>${d.count} sessions</td>
        <td>${formatTime(d.seconds)}</td>
        <td>${Math.round((d.seconds/totalSeconds)*100)}%</td>
      </tr>`).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Drumnasium — Estatísticas</title>
        <style>
          body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 900px; margin: 0 auto; }
          h1 { color: #FF5500; font-size: 28px; margin-bottom: 4px; }
          h2 { font-size: 16px; color: #555; font-weight: normal; margin-bottom: 24px; }
          .summary { display: flex; gap: 20px; margin-bottom: 28px; }
          .card { background: #f5f5f5; border-radius: 10px; padding: 16px 24px; text-align: center; flex: 1; }
          .card .val { font-size: 28px; font-weight: 800; color: #FF5500; }
          .card .lbl { font-size: 12px; color: #888; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
          th { background: #FF5500; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          tr:nth-child(even) td { background: #fafafa; }
          h3 { font-size: 16px; color: #333; margin: 24px 0 12px; border-bottom: 2px solid #FF5500; padding-bottom: 6px; }
          .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; }
        </style>
      </head>
      <body>
        <h1>🥁 DRUMNASIUM</h1>
        <h2>Training Report — ${periodLabel}</h2>

        <div class="summary">
          <div class="card"><div class="val">${totalSessions}</div><div class="lbl">Sessions</div></div>
          <div class="card"><div class="val">${formatTime(totalSeconds)}</div><div class="lbl">Total Time</div></div>
          <div class="card"><div class="val">${maxBpm||'—'}</div><div class="lbl">Max BPM</div></div>
        </div>

        <h3>Summary by Category</h3>
        <table>
          <thead><tr><th>Category</th><th>Sessions</th><th>Time</th><th>%</th></tr></thead>
          <tbody>${catRows}</tbody>
        </table>

        <h3>Logs</h3>
        <table>
          <thead><tr><th>Date</th><th>Exercise</th><th>Category</th><th>Duration</th><th>BPM</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
                    <div key={d.name} style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background:d.color, flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#C4B9AC' }}>{d.emoji} {d.name}</div>
                        <div style={{ fontSize:10, color:'#5A5248' }}>{formatTime(d.seconds)} · {d.count} sess.</div>
                      </div>
                      <div style={{ fontFamily:"'Barlow Condensed'", fontSize:14, fontWeight:800, color:d.color }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bar chart by category */}
        {chartData.length > 0 && (
          <div className="section fu" style={{ animationDelay:'0.1s', opacity:0 }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:12, fontWeight:800, letterSpacing:2, color:'#7A7268', marginBottom:14, textTransform:'uppercase' }}>
              Category Sessions
            </div>
            {chartData.map(d => {
              const pct = totalSessions > 0 ? (d.count / totalSessions) * 100 : 0;
              const exportPDF = () => {
    const periodLabel = period === 'week' ? 'This Week'
      : period === 'month' ? 'This Month'
      : `${customStart} to ${customEnd}`;

    const rows = filteredSessions.map(s => {
      const info = getExerciseInfo(s);
      return `
        <tr>
          <td>${new Date(s.completed_at).toLocaleDateString('pt-PT')}</td>
          <td>${info.title}</td>
          <td style="color:${info.catColor}">${info.catEmoji} ${info.catName}</td>
          <td>${formatTime(s.duration_seconds||0)}</td>
          <td>${s.bpm || '—'} BPM</td>
          ${s.notes ? `<td><em>${s.notes}</em></td>` : '<td>—</td>'}
        </tr>`;
    }).join('');

    const catRows = chartData.map(d => `
      <tr>
        <td>${d.emoji} ${d.name}</td>
        <td>${d.count} sessions</td>
        <td>${formatTime(d.seconds)}</td>
        <td>${Math.round((d.seconds/totalSeconds)*100)}%</td>
      </tr>`).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Drumnasium — Estatísticas</title>
        <style>
          body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 900px; margin: 0 auto; }
          h1 { color: #FF5500; font-size: 28px; margin-bottom: 4px; }
          h2 { font-size: 16px; color: #555; font-weight: normal; margin-bottom: 24px; }
          .summary { display: flex; gap: 20px; margin-bottom: 28px; }
          .card { background: #f5f5f5; border-radius: 10px; padding: 16px 24px; text-align: center; flex: 1; }
          .card .val { font-size: 28px; font-weight: 800; color: #FF5500; }
          .card .lbl { font-size: 12px; color: #888; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
          th { background: #FF5500; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          tr:nth-child(even) td { background: #fafafa; }
          h3 { font-size: 16px; color: #333; margin: 24px 0 12px; border-bottom: 2px solid #FF5500; padding-bottom: 6px; }
          .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; }
        </style>
      </head>
      <body>
        <h1>🥁 DRUMNASIUM</h1>
        <h2>Training Report — ${periodLabel}</h2>

        <div class="summary">
          <div class="card"><div class="val">${totalSessions}</div><div class="lbl">Sessions</div></div>
          <div class="card"><div class="val">${formatTime(totalSeconds)}</div><div class="lbl">Total Time</div></div>
          <div class="card"><div class="val">${maxBpm||'—'}</div><div class="lbl">Max BPM</div></div>
        </div>

        <h3>Summary by Category</h3>
        <table>
          <thead><tr><th>Category</th><th>Sessions</th><th>Time</th><th>%</th></tr></thead>
          <tbody>${catRows}</tbody>
        </table>

        <h3>Logs</h3>
        <table>
          <thead><tr><th>Date</th><th>Exercise</th><th>Category</th><th>Duration</th><th>BPM</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
                <div key={d.name} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5 }}>
                    <span style={{ color:'#C4B9AC', fontWeight:600 }}>{d.emoji} {d.name}</span>
                    <span style={{ color:d.color, fontFamily:"'Barlow Condensed'", fontWeight:700 }}>{d.count} sess. · {formatTime(d.seconds)}</span>
                  </div>
                  <div style={{ height:6, background:'#2A2520', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:d.color, borderRadius:3, transition:'width 1s ease' }}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Logs */}
        <div className="fu" style={{ animationDelay:'0.15s', opacity:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:12, fontWeight:800, letterSpacing:2, color:'#7A7268', textTransform:'uppercase' }}>
              Logs ({filteredSessions.length}{period === 'custom' && customStart && customEnd ? ` · ${customStart} → ${customEnd}` : ''})
            </div>
          </div>

          {/* Category filter */}
          <div style={{ display:'flex', gap:6, marginBottom:12, overflowX:'auto', paddingBottom:4, marginLeft:-16, paddingLeft:16, marginRight:-16, paddingRight:16 }}>
            <button onClick={() => setFilterCat('all')} style={{ padding:'5px 12px', borderRadius:20, flexShrink:0, background:filterCat==='all'?'#FF5500':'#1C1916', color:filterCat==='all'?'#fff':'#7A7268', border:`1px solid ${filterCat==='all'?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1 }}>ALL</button>
            {chartData.map(d => (
              <button key={d.name} onClick={() => setFilterCat(filterCat===d.name?'all':d.name)}
                style={{ padding:'5px 12px', borderRadius:20, flexShrink:0, background:filterCat===d.name?d.color:'#1C1916', color:filterCat===d.name?'#fff':'#7A7268', border:`1px solid ${filterCat===d.name?d.color:'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1, whiteSpace:'nowrap' }}>
                {d.emoji} {d.name}
              </button>
            ))}
          </div>

          {filteredSessions.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#3A3530', fontSize:13, fontStyle:'italic' }}>
              {loading ? 'Loading…' : 'No sessions in this period.'}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {filteredSessions.map(s => {
                const info = getExerciseInfo(s);
                const exportPDF = () => {
    const periodLabel = period === 'week' ? 'This Week'
      : period === 'month' ? 'This Month'
      : `${customStart} to ${customEnd}`;

    const rows = filteredSessions.map(s => {
      const info = getExerciseInfo(s);
      return `
        <tr>
          <td>${new Date(s.completed_at).toLocaleDateString('pt-PT')}</td>
          <td>${info.title}</td>
          <td style="color:${info.catColor}">${info.catEmoji} ${info.catName}</td>
          <td>${formatTime(s.duration_seconds||0)}</td>
          <td>${s.bpm || '—'} BPM</td>
          ${s.notes ? `<td><em>${s.notes}</em></td>` : '<td>—</td>'}
        </tr>`;
    }).join('');

    const catRows = chartData.map(d => `
      <tr>
        <td>${d.emoji} ${d.name}</td>
        <td>${d.count} sessions</td>
        <td>${formatTime(d.seconds)}</td>
        <td>${Math.round((d.seconds/totalSeconds)*100)}%</td>
      </tr>`).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8"/>
        <title>Drumnasium — Estatísticas</title>
        <style>
          body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 900px; margin: 0 auto; }
          h1 { color: #FF5500; font-size: 28px; margin-bottom: 4px; }
          h2 { font-size: 16px; color: #555; font-weight: normal; margin-bottom: 24px; }
          .summary { display: flex; gap: 20px; margin-bottom: 28px; }
          .card { background: #f5f5f5; border-radius: 10px; padding: 16px 24px; text-align: center; flex: 1; }
          .card .val { font-size: 28px; font-weight: 800; color: #FF5500; }
          .card .lbl { font-size: 12px; color: #888; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
          th { background: #FF5500; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 9px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
          tr:nth-child(even) td { background: #fafafa; }
          h3 { font-size: 16px; color: #333; margin: 24px 0 12px; border-bottom: 2px solid #FF5500; padding-bottom: 6px; }
          .footer { margin-top: 32px; font-size: 11px; color: #aaa; text-align: center; }
        </style>
      </head>
      <body>
        <h1>🥁 DRUMNASIUM</h1>
        <h2>Training Report — ${periodLabel}</h2>

        <div class="summary">
          <div class="card"><div class="val">${totalSessions}</div><div class="lbl">Sessions</div></div>
          <div class="card"><div class="val">${formatTime(totalSeconds)}</div><div class="lbl">Total Time</div></div>
          <div class="card"><div class="val">${maxBpm||'—'}</div><div class="lbl">Max BPM</div></div>
        </div>

        <h3>Summary by Category</h3>
        <table>
          <thead><tr><th>Category</th><th>Sessions</th><th>Time</th><th>%</th></tr></thead>
          <tbody>${catRows}</tbody>
        </table>

        <h3>Logs</h3>
        <table>
          <thead><tr><th>Date</th><th>Exercise</th><th>Category</th><th>Duration</th><th>BPM</th><th>Notes</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">Generated on ${new Date().toLocaleDateString('en-GB')} · Drumnasium Percussion Training</div>
      </body>
      </html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
                  <div key={s.id} onClick={() => s.exercise_id && navigate(`/exercise/${s.exercise_id}`)}
                    style={{ background:'#1C1916', border:'1px solid #2A2520', borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, cursor: s.exercise_id ? 'pointer' : 'default' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:info.catColor, flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{info.title}</div>
                      <div style={{ fontSize:11, color:'#5A5248' }}>
                        <span style={{ color:info.catColor, fontWeight:600 }}>{info.catEmoji} {info.catName}</span>
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:12, color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:700 }}>⏱ {formatTime(s.duration_seconds||0)}</div>
                      <div style={{ fontSize:11, color:'#7A7268', marginTop:2 }}>♩ {s.bpm}</div>
                      <div style={{ fontSize:10, color:'#4A4640', marginTop:1 }}>{formatDate(s.completed_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <BottomNav/>
    </div>
  );
}
